"use client";

import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import Image from "next/image";

// Animated hamburger component
import { MenuToggleIcon } from "@/components/ui/MenuToggleIcon";

// Navigation links with section IDs
const navLinks = [
  { name: "Features", href: "#features", sectionId: "features" },
  { name: "Billing ERP", href: "#billing", sectionId: "billing" },
  { name: "Notices", href: "#notice", sectionId: "notice" },
  { name: "Committee", href: "#committee", sectionId: "committee" },
  { name: "Pricing", href: "#pricing", sectionId: "pricing" },
];

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  // ScrollSpy: Track which section is currently in viewport
  const handleScrollSpy = useCallback(() => {
    // Update header background
    setScrolled(window.scrollY > 20);

    // Find active section based on scroll position
    const scrollPosition = window.scrollY + 150; // Offset for header height

    // Check each section from bottom to top to find the current one
    for (let i = navLinks.length - 1; i >= 0; i--) {
      const section = document.getElementById(navLinks[i].sectionId);
      if (section) {
        const sectionTop = section.offsetTop;
        if (scrollPosition >= sectionTop) {
          setActiveSection(navLinks[i].sectionId);
          return;
        }
      }
    }

    // If we're at the very top, no section is active
    if (window.scrollY < 100) {
      setActiveSection(null);
    }
  }, []);

  useEffect(() => {
    // Throttled scroll handler for performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScrollSpy();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Initial check on mount (deferred to next frame)
    window.requestAnimationFrame(handleScrollSpy);

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScrollSpy]);

  const toggleMenu = () => setIsOpen(!isOpen);

  // Scroll to top handler for logo
  const handleScrollToTop = (e: React.MouseEvent) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Smooth Scroll Handler for Anchor Links
  const handleScrollTo = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
  ) => {
    e.preventDefault();
    const targetId = href.replace("#", "");
    const elem = document.getElementById(targetId);
    if (elem) {
      elem.scrollIntoView({ behavior: "smooth" });
      setIsOpen(false); // Close mobile menu on click
    }
  };

  // Determine which item should show the stroke (hover takes priority over active)
  const strokeTarget = hoveredItem || activeSection;

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ease-in-out ${
          scrolled
            ? "bg-black/30 backdrop-blur-2xl border-b border-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.1)] py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between">
          {/* LOGO - Click to Scroll to Top */}
          <button
            onClick={handleScrollToTop}
            className="flex items-center gap-3 group cursor-pointer"
            aria-label="Scroll to top"
          >
            <div className="w-10 h-10 transition-transform group-hover:scale-105">
              <Image
                src="/brand/logo.svg"
                alt="SmartDwara Logo"
                width={40}
                height={40}
                priority
                className="w-10 h-10"
              />
            </div>
            <div className="flex items-center">
              <span className="text-white font-clash font-semibold text-2xl tracking-tight group-hover:text-gray-200 transition-colors">
                Smart
              </span>
              <span className="font-clash font-bold text-2xl tracking-tight text-transparent bg-clip-text bg-linear-to-r from-orange-500 to-amber-500">
                Dwara
              </span>
            </div>
          </button>

          {/* DESKTOP NAV with Shimmer Stroke */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = activeSection === link.sectionId;
              const isHovered = hoveredItem === link.sectionId;
              const showStroke = strokeTarget === link.sectionId;

              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => handleScrollTo(e, link.href)}
                  onMouseEnter={() => setHoveredItem(link.sectionId)}
                  onMouseLeave={() => setHoveredItem(null)}
                  className="relative px-4 py-2 group"
                >
                  {/* Nav Text */}
                  <span
                    className={`font-clash text-l font-semibold tracking-wide transition-colors duration-200 ${
                      isActive || isHovered
                        ? "text-white"
                        : "text-gray-400 group-hover:text-white"
                    }`}
                  >
                    {link.name}
                  </span>

                  {/* Shimmer Stroke - Animated with layoutId */}
                  {showStroke && (
                    <motion.div
                      layoutId="navbar-stroke"
                      className="absolute bottom-0 left-2 right-2 h-0.5 bg-linear-to-r from-orange-500 to-amber-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.6)]"
                      initial={false}
                      transition={{
                        type: "spring",
                        stiffness: 500,
                        damping: 35,
                      }}
                    />
                  )}
                </a>
              );
            })}
          </nav>

          {/* CTA BUTTON & MOBILE TOGGLE */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                const elem = document.getElementById("contact");
                if (elem) elem.scrollIntoView({ behavior: "smooth" });
              }}
              className="hidden md:flex items-center gap-2 bg-orange-500 hover:bg-white hover:text-orange-500 text-white px-6 py-3 rounded-full font-clash font-semibold uppercase text-sm transition-all active:scale-95 shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40"
            >
              Book Demo <ArrowRight className="w-4 h-4" />
            </button>

            {/* Animated Mobile Hamburger */}
            <button
              onClick={toggleMenu}
              className="md:hidden p-2 text-white bg-white/5 hover:bg-white/10 rounded-full transition-colors backdrop-blur-md flex items-center justify-center"
              aria-label="Toggle Menu"
            >
              <MenuToggleIcon open={isOpen} className="w-7 h-7" />
            </button>
          </div>
        </div>
      </header>

      {/* MOBILE MENU OVERLAY */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, backdropFilter: "blur(0px)" }}
            animate={{ opacity: 1, backdropFilter: "blur(20px)" }}
            exit={{ opacity: 0, backdropFilter: "blur(0px)" }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-black/60 pt-28 px-6 md:hidden"
          >
            <div className="flex flex-col gap-4">
              {navLinks.map((link, i) => {
                const isActive = activeSection === link.sectionId;
                return (
                  <motion.a
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    key={link.name}
                    href={link.href}
                    onClick={(e) => handleScrollTo(e, link.href)}
                    className={`text-3xl font-bold transition-colors relative ${
                      isActive
                        ? "text-orange-500"
                        : "text-white/80 hover:text-white"
                    }`}
                  >
                    {link.name}
                    {/* Mobile Active Indicator */}
                    {isActive && (
                      <motion.div
                        layoutId="mobile-stroke"
                        className="absolute -left-4 top-1/2 -translate-y-1/2 w-1 h-6 bg-linear-to-b from-orange-500 to-amber-500 rounded-full shadow-[0_0_8px_rgba(249,115,22,0.6)]"
                      />
                    )}
                  </motion.a>
                );
              })}
              <motion.button
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                onClick={() => {
                  setIsOpen(false);
                  const elem = document.getElementById("contact");
                  if (elem) elem.scrollIntoView({ behavior: "smooth" });
                }}
                className="w-full py-4 bg-orange-500 text-white font-clash font-bold rounded-2xl mt-8 flex items-center justify-center gap-2 shadow-lg shadow-orange-500/20"
              >
                Book a Demo <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
